import React, { useCallback, useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { GAMEPLAY_CONFIG } from '../../../shared/constants/GameplayConfig';
import { TomeButton } from '../primitives/tome/TomeButton';
import '../primitives/tome/tome.css';
import { FONTS } from '../theme';
import { useHudTomeVibe } from './useHudTomeVibe';

/**
 * PartyInvitePrompt — the HUD's party invite prompt and party notices
 * (stream playtest 2026-10-08, plan 23 row 41).
 *
 * The invite used to be heard only by `useParty()` inside the Party window, so
 * a player who did not have that window open never saw it — the server sent
 * `partyInviteReceived` and nothing listened. This is mounted with the other
 * always-on HUD overlays (src/main.tsx), so the invite reaches the player
 * wherever they are. It shows who invited them, Accept / Decline, and a bar
 * that runs out with the server's invite expiry, at which point it declines
 * for them (the server would refuse a late accept anyway).
 *
 * It also shows the short party notices nobody saw before: someone joined,
 * your invite was declined, or why an invite failed.
 *
 * Listeners go through useColyseusMessages, which rebinds when the room object
 * changes (reconnect); a portal reloads the page and remounts this, and the
 * server drops a pending invite when either player leaves the room.
 */

const NOTICE_MS = 4000;

const PARTY_ERROR_TEXT: Record<string, string> = {
  invalid_target: 'You can’t invite that player.',
  player_not_found: 'That player is no longer here.',
  target_in_party: 'That player is already in a party.',
  not_leader: 'Only the party leader can do that.',
  party_full: 'The party is full.',
  no_invite: 'That invite has expired.',
  party_disbanded: 'That party has disbanded.',
};

export interface IncomingPartyInvite {
  inviterName: string;
  inviterSessionId?: string;
  expiresAt: number;
  zoneId: string;
}

function currentRoom(): any {
  return (globalThis as any).nm?.getRoom?.() ?? null;
}

function sendParty(type: 'partyAccept' | 'partyDecline'): void {
  try {
    currentRoom()?.send(type, {});
  } catch {
    // A dropped send must not take the HUD down; the server expires the invite.
  }
}

export const PartyInvitePrompt: React.FC = () => {
  const [invite, setInvite] = useState<IncomingPartyInvite | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useColyseusMessages({
    partyInviteReceived: (msg: any) => {
      const room = currentRoom();
      setInvite({
        inviterName: typeof msg?.inviterName === 'string' && msg.inviterName ? msg.inviterName : 'Someone',
        inviterSessionId: msg?.inviterSessionId,
        expiresAt: Date.now() + GAMEPLAY_CONFIG.party.inviteExpiryMs,
        zoneId: room?.state?.mapId ?? '',
      });
    },
    partyMemberJoined: (msg: any) => {
      const mine = msg?.sessionId && msg.sessionId === currentRoom()?.sessionId;
      setNotice(mine ? 'You joined the party.' : `${msg?.name ?? 'Someone'} joined the party.`);
    },
    partyInviteDeclined: (msg: any) => setNotice(`${msg?.name ?? 'They'} declined your party invite.`),
    partyError: (msg: any) => {
      setNotice(PARTY_ERROR_TEXT[msg?.reason] ?? 'The party invite didn’t go through.');
      if (msg?.reason === 'no_invite' || msg?.reason === 'party_disbanded') setInvite(null);
    },
  });

  const answer = useCallback((type: 'partyAccept' | 'partyDecline') => {
    sendParty(type);
    setInvite(null);
  }, []);

  // Decline for the player when the invite runs out.
  useEffect(() => {
    if (!invite) return undefined;
    const t = setTimeout(() => answer('partyDecline'), Math.max(0, invite.expiresAt - Date.now()));
    return () => clearTimeout(t);
  }, [invite, answer]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(t);
  }, [notice]);

  const tome = useHudTomeVibe(invite?.zoneId ?? currentRoom()?.state?.mapId);
  if (!invite && !notice) return null;

  return (
    <div
      className={tome.className}
      style={{
        ...tome.style,
        position: 'fixed',
        top: 100,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9997,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
        fontFamily: FONTS.ui,
      }}
    >
      {invite && (
        <PartyInviteCard
          invite={invite}
          onAccept={() => answer('partyAccept')}
          onDecline={() => answer('partyDecline')}
        />
      )}
      {notice && (
        <div
          data-testid="party-notice"
          role="status"
          style={{
            padding: '4px 12px',
            background: 'linear-gradient(180deg, var(--tome-hud-ground-hi), var(--tome-hud-ground))',
            border: '1px solid var(--tome-hud-rule)',
            borderRadius: 'var(--tome-chip-cut-1)',
            color: 'var(--tome-hud-ink)',
            fontSize: 12,
            whiteSpace: 'nowrap',
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.35)',
          }}
        >
          {notice}
        </div>
      )}
    </div>
  );
};

export const PartyInviteCard: React.FC<{
  invite: IncomingPartyInvite;
  onAccept: () => void;
  onDecline: () => void;
}> = ({ invite, onAccept, onDecline }) => {
  // The bar runs on CSS, not a React ticker: the prompt renders once per invite.
  const remainingMs = Math.max(0, invite.expiresAt - Date.now());
  return (
    <section
      data-testid="party-invite-prompt"
      role="dialog"
      aria-label={`Party invite from ${invite.inviterName}`}
      style={{
        pointerEvents: 'auto',
        minWidth: 300,
        padding: '12px 16px 10px',
        // Opaque (the cover colours the HUD ground mixes from): it asks for an answer.
        background: 'linear-gradient(180deg, var(--tome-cover-hi), var(--tome-cover-lo))',
        border: '1px solid var(--tome-hud-rule)',
        borderRadius: 'var(--tome-chip-cut-1)',
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.35), 0 6px 18px rgba(0, 0, 0, 0.5)',
        color: 'var(--tome-hud-ink)',
      }}
    >
      <div style={{ fontFamily: FONTS.display, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--tome-hud-ink-soft)' }}>
        Party invite
      </div>
      <div style={{ margin: '4px 0 10px', fontSize: 14 }}>
        <strong style={{ fontFamily: FONTS.display, color: 'var(--tome-gilt-bright)' }}>{invite.inviterName}</strong>
        <span> invites you to adventure together.</span>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <TomeButton tone="secondary" onClick={onDecline} style={{ minHeight: 34 }}>Decline</TomeButton>
        <TomeButton tone="primary" onClick={onAccept} style={{ minHeight: 34 }}>Accept</TomeButton>
      </div>
      <div aria-hidden="true" style={{ marginTop: 10, height: 3, background: 'rgba(0, 0, 0, 0.35)', borderRadius: 2, overflow: 'hidden' }}>
        <div
          data-testid="party-invite-timer"
          style={{
            height: '100%',
            background: 'var(--tome-hud-rule)',
            transformOrigin: 'left center',
            animation: `partyInviteTimer ${remainingMs}ms linear forwards`,
          }}
        />
      </div>
      <style>{'@keyframes partyInviteTimer { from { transform: scaleX(1); } to { transform: scaleX(0); } }'}</style>
    </section>
  );
};
